import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  TextField,
  Grid,
  Paper,
  Stack,
  CircularProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import { DirectChipKey } from '../../types/seo';
import { generateRefinedSeoContent } from '../../utils/seoAIEngine';

interface RefineRegenerateModalProps {
  open: boolean;
  onClose: () => void;
  entityType: 'PROPERTY' | 'LOCALITY' | 'HOMEPAGE';
  currentTitle: string;
  currentDescription: string;
  currentRichContent?: string;
  currentFaqs?: { question: string; answer: string }[];
  projectName?: string;
  typology?: string;
  locality?: string;
  possessionStage?: string;
  carpetArea?: number;
  priceInr?: number;
  reraNumber?: string;
  onApply: (refined: {
    title: string;
    description: string;
    richContent?: string;
    faqs?: { question: string; answer: string }[];
  }) => void;
}

const DIRECTIVE_CHIPS: { key: DirectChipKey; label: string; icon: string }[] = [
  { key: 'rera_ready', label: 'Emphasize GujRERA Approved & Ready to Move Status', icon: '🏗️' },
  { key: 'dec_2026_timeline', label: 'Highlight Dec 2026 Possession & Construction-Linked Plan', icon: '⏳' },
  { key: 'metro_tech', label: 'Highlight Proximity to Metro & Diamond Bourse', icon: '🚇' },
  { key: 'rental_roi', label: 'Feature High Rental Yield & Investment ROI in Surat', icon: '📈' },
  { key: 'luxury_amenities', label: 'Emphasize Luxury Amenities (Clubhouse, Infinity Pool)', icon: '🏊' },
  { key: 'price_loan', label: 'Add Home Loan EMI & Bank Tie-up FAQs', icon: '🏦' },
  { key: 'vastu_facing', label: 'Focus on 100% Vastu Compliant East/North Facing', icon: '📐' },
  { key: 'strict_char_limit', label: 'Strictly keep Meta Description under 150 characters', icon: '📏' },
];

export default function RefineRegenerateModal({
  open,
  onClose,
  entityType,
  currentTitle,
  currentDescription,
  currentRichContent = '',
  currentFaqs = [],
  projectName,
  typology,
  locality,
  possessionStage,
  carpetArea,
  priceInr,
  reraNumber,
  onApply,
}: RefineRegenerateModalProps) {
  const [selectedDirectives, setSelectedDirectives] = useState<DirectChipKey[]>([
    'strict_char_limit',
    possessionStage?.toLowerCase().includes('dec 2026') ? 'dec_2026_timeline' : 'rera_ready',
  ]);
  const [customPrompt, setCustomPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState<{
    title: string;
    description: string;
    richContent: string;
    faqs: { question: string; answer: string }[];
    appliedDirectives: string[];
  } | null>(null);

  const toggleDirective = (key: DirectChipKey) => {
    setSelectedDirectives((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      const result = generateRefinedSeoContent({
        entityType,
        title: currentTitle,
        description: currentDescription,
        directives: selectedDirectives,
        customPrompt,
        projectName,
        typology,
        locality,
        possessionStage,
        carpetArea,
        priceInr,
        reraNumber,
      });

      setGeneratedResult({
        title: result.refinedTitle,
        description: result.refinedDescription,
        richContent: result.refinedRichContent,
        faqs: result.refinedFaqs,
        appliedDirectives: result.appliedDirectives,
      });
      setIsGenerating(false);
    }, 600);
  };

  const handleApply = () => {
    if (generatedResult) {
      onApply({
        title: generatedResult.title,
        description: generatedResult.description,
        richContent: generatedResult.richContent,
        faqs: generatedResult.faqs,
      });
      onClose();
    }
  };

  const getCharMeterColor = (length: number, min: number, max: number) => {
    if (length >= min && length <= max) return '#2e7d32'; // green
    if (length > max) return '#d32f2f'; // red
    return '#ed6c02'; // orange
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '8px',
              bgcolor: 'rgba(157,111,50,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9d6f32',
            }}
          >
            <AutoAwesomeIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, color: '#1a1a1a' }}>
              Interactive Merchandiser: Refine & Re-Generate SEO
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Target: Surat, Gujarat · Project: {projectName || 'Swarna Group'} ({possessionStage || 'All Stages'})
            </Typography>
          </Box>
        </Stack>
        <Button size="small" onClick={onClose} sx={{ minWidth: 32, p: 0.5, color: '#888' }}>
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        {/* Step 1: Quick Directive Chips */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <TuneIcon sx={{ fontSize: 18, color: '#9d6f32' }} />
            1. Select Merchandising & Marketing Directives
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {DIRECTIVE_CHIPS.map((chip) => {
              const isSelected = selectedDirectives.includes(chip.key);
              return (
                <Chip
                  key={chip.key}
                  label={`${chip.icon} ${chip.label}`}
                  onClick={() => toggleDirective(chip.key)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: isSelected ? 600 : 500,
                    fontSize: 12,
                    cursor: 'pointer',
                    borderColor: isSelected ? '#9d6f32' : '#e0e0e0',
                    bgcolor: isSelected ? '#9d6f32' : '#fff',
                    color: isSelected ? '#fff' : '#444',
                    '&:hover': {
                      bgcolor: isSelected ? '#7a5525' : '#f5f5f5',
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Step 2: Custom Prompt Injection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            2. Custom Broker / Marketing Directive (Optional)
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g., Focus on NRI investors from USA/UK looking for Surat airport proximity or emphasize 24x7 security for joint families."
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            multiline
            rows={2}
            sx={{ bgcolor: '#fff' }}
          />
        </Box>

        {/* Generate Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleGenerate}
            disabled={isGenerating}
            sx={{
              px: 4,
              py: 1,
              bgcolor: '#9d6f32',
              fontWeight: 700,
              fontSize: 14,
              boxShadow: '0 4px 14px rgba(157,111,50,0.3)',
              '&:hover': { bgcolor: '#7a5525' },
            }}
          >
            {isGenerating ? 'Synthesizing Surat SXO Engine...' : 'Run Real Estate AI Re-Generation'}
          </Button>
        </Box>

        {/* Step 3: Side-by-Side Diff Comparison */}
        {generatedResult && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <CompareArrowsIcon sx={{ color: '#9d6f32' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Side-by-Side Verification (Current vs. AI Synthesized)
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {/* Current Version */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: '#fafafa',
                    borderColor: '#e0e0e0',
                    height: '100%',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>
                    Current Metadata
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                      SEO Title ({currentTitle.length}/60 chars):
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', mt: 0.3, mb: 1.5 }}>
                      {currentTitle || '—'}
                    </Typography>

                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                      Meta Description ({currentDescription.length} chars):
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#444', mt: 0.3, fontSize: 13, lineHeight: 1.4 }}>
                      {currentDescription || '—'}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* AI Generated Version */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: '#fcfbf8',
                    borderColor: '#9d6f32',
                    borderWidth: 2,
                    height: '100%',
                    borderRadius: 2,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#9d6f32', textTransform: 'uppercase' }}>
                      ✨ AI Generated & Deterministically Clamped
                    </Typography>
                    <Chip label="Ready to Apply" color="success" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
                  </Stack>

                  <Box sx={{ mt: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                        SEO Title (≤60 chars):
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: getCharMeterColor(generatedResult.title.length, 30, 60),
                        }}
                      >
                        {generatedResult.title.length}/60 chars
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a1a', mt: 0.3, mb: 1.5 }}>
                      {generatedResult.title}
                    </Typography>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#555' }}>
                        Meta Description (125-155 chars):
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: getCharMeterColor(generatedResult.description.length, 125, 155),
                        }}
                      >
                        {generatedResult.description.length} chars (Optimal)
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#222', mt: 0.3, fontSize: 13, lineHeight: 1.4 }}>
                      {generatedResult.description}
                    </Typography>

                    {generatedResult.faqs.length > 0 && (
                      <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #e8e8e8' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#9d6f32' }}>
                          + {generatedResult.faqs.length} High-Intent Buyer FAQs Injected
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#fafaf9', borderTop: '1px solid #e8e8e8' }}>
        <Button onClick={onClose} sx={{ color: '#666', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!generatedResult}
          onClick={handleApply}
          startIcon={<CheckIcon />}
          sx={{
            bgcolor: '#2e7d32',
            fontWeight: 700,
            '&:hover': { bgcolor: '#1b5e20' },
          }}
        >
          Accept & Apply to Editor
        </Button>
      </DialogActions>
    </Dialog>
  );
}
